import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";

// --- 🎨 COLOR CONTROL PANEL ---
const COLORS = {
  headerBg: "#0080ff",
  headerText: "#ffffff",
  rowHover: "rgba(0, 128, 255, 0.08)",
  emptyCellText: "#000000",
  heatmapBase: "26, 35, 126",
  staffName: "#333",
  borderColor: "#f0f0f0"
};

const StaffAnalyticsTable = ({ analyticsList }) => {
  if (!analyticsList || analyticsList.length === 0) return null;

  // --- 📅 WEEK LOGIC ---
  const getWeekRange = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const monday = new Date(now);
    // Align to the most recent Monday
    monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    
    monday.setHours(0, 0, 0, 0);
    saturday.setHours(23, 59, 59, 999);
    return { monday, saturday };
  };

  const { monday, saturday } = getWeekRange();

  // --- 🔍 FILTER: Only show staff who contributed THIS week ---
  const activeThisWeek = analyticsList.filter((item) => {
    // Check all workstations for this person
    return Object.values(item.days || {}).some((daySet) => {
      return Array.from(daySet).some((dateStr) => {
        const dt = new Date(dateStr);
        return dt >= monday && dt <= saturday;
      });
    });
  });

  // Calculate maxCount based on the filtered list for proper heatmap scaling
  const maxCount = Math.max(
    ...activeThisWeek.flatMap((item) => Object.values(item.counts)),
    1
  );

  const getCellStyles = (count) => {
    if (count === 0) return { backgroundColor: "transparent", color: COLORS.emptyCellText };
    const opacity = Math.max(0.1, (count / maxCount));
    return {
      backgroundColor: `rgba(${COLORS.heatmapBase}, ${opacity})`,
      color: opacity > 0.6 ? "#fff" : `rgb(${COLORS.heatmapBase})`,
      transition: "all 0.3s ease",
    };
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  return (
<TableContainer 
      component={Paper} 
      elevation={0} 
      sx={{ 
        backgroundColor: "transparent", 
        maxHeight: { xs: 500, md: 600 }, // Shorter height on mobile
        borderRadius: "12px",
        overflow: "auto", // Allows both vertical and horizontal scrolling
        border: `1px solid ${COLORS.borderColor}`,
        // Enable smooth scrolling on iOS
        WebkitOverflowScrolling: "touch"
      }}
    >
      <Table stickyHeader sx={{ minWidth: 650 }}> {/* Ensures table doesn't squish too small */}
        <TableHead>
          <TableRow>
            <TableCell 
              sx={{ 
                backgroundColor: COLORS.headerBg, 
                color: COLORS.headerText, 
                fontWeight: 800, 
                textTransform: "uppercase", 
                fontSize: "0.75rem", 
                letterSpacing: "1px",
                position: "sticky", // Keep names visible when scrolling right
                left: 0,
                zIndex: 3 // Higher than other sticky headers
              }}
            >
              Name
            </TableCell>
            {Object.keys(analyticsList[0].counts).map((ws) => (
              <TableCell 
                key={ws} 
                align="center" 
                sx={{ 
                  backgroundColor: COLORS.headerBg, 
                  color: COLORS.headerText, 
                  fontWeight: 800, 
                  textTransform: "uppercase", 
                  fontSize: { xs: "0.65rem", md: "0.75rem" }, // Smaller text on mobile
                  letterSpacing: "1px",
                  zIndex: 2
                }}
              >
                {ws}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {activeThisWeek.map((item) => (
            <TableRow 
              key={item.staffName}
              sx={{ "&:hover": { backgroundColor: COLORS.rowHover }, transition: "background-color 0.2s" }}
            >
              <TableCell 
                sx={{ 
                  borderBottom: `1px solid ${COLORS.borderColor}`,
                  backgroundColor: "#fff", // Solid background for sticky visibility
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  whiteSpace: "nowrap" // Prevents names from wrapping to multiple lines
                }}
              >
                <Typography variant="body2" fontWeight="600" color={COLORS.staffName} sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                  {item.staffName}
                </Typography>
              </TableCell>

              {Object.entries(item.counts).map(([ws, count]) => {
                const daySet = item.days?.[ws] || new Set();
                const dayArray = [...daySet].filter((d) => {
                  const dt = new Date(d);
                  return dt >= monday && dt <= saturday;
                }).sort((a, b) => new Date(a) - new Date(b));

                const displayCount = dayArray.length;
                const tooltipText = displayCount > 0 ? dayArray.map(formatDate).join(", ") : "No activity";
                const cellStyle = getCellStyles(displayCount);

                return (
                  <Tooltip key={ws} title={`Dates: ${tooltipText}`} arrow enterTouchDelay={0}> 
                    <TableCell
                      align="center"
                      sx={{
                        ...cellStyle,
                        fontWeight: "800",
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        borderBottom: `1px solid ${COLORS.borderColor}`,
                        cursor: "help",
                        minWidth: { xs: "80px", md: "unset" }, // Prevents cells from being too narrow
                        "&:hover": {
                          transform: { md: "scale(1.1)", xs: "none" }, // Disable scale on touch to avoid layout shifts
                          zIndex: 1,
                          borderRadius: "4px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                        }
                      }}
                    >
                      {displayCount}
                    </TableCell>
                  </Tooltip>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StaffAnalyticsTable;